import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { enforceClarityExclusion } from "@/lib/clarity";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const userRef = useRef(null);
  const expiringRef = useRef(false);
  userRef.current = user;

  // Stop Clarity recording the moment we know the signed-in user is internal/staff
  // (super_admin, developer, @recoveros.in, demo/excluded tenants). Covers the case
  // where they started anonymous on /login and Clarity was already running.
  useEffect(() => { enforceClarityExclusion(user); }, [user]);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      try { localStorage.setItem("user", JSON.stringify(res.data)); } catch { /* noop */ }
    } catch {
      setUser(null);
      try { localStorage.removeItem("user"); } catch { /* noop */ }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Clean session-expiry handling: when an authenticated API call returns 401,
  // clear local auth and bounce to login with a clear message (once).
  useEffect(() => {
    const onUnauthorized = () => {
      if (!userRef.current || expiringRef.current) return; // ignore logged-out probes / duplicates
      expiringRef.current = true;
      localStorage.removeItem("auth_token");
      localStorage.removeItem("active_tenant_id");
      localStorage.removeItem("user");
      setUser(null);
      toast.error("Your session has expired. Please sign in again.");
      const onLogin = window.location.pathname === "/login" || window.location.pathname === "/signin";
      if (!onLogin) {
        setTimeout(() => window.location.assign("/login"), 600);
      }
    };
    window.addEventListener("recoveros:unauthorized", onUnauthorized);
    return () => window.removeEventListener("recoveros:unauthorized", onUnauthorized);
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    localStorage.removeItem("auth_token");
    localStorage.removeItem("active_tenant_id");
    localStorage.removeItem("user");
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
